import type { App, DirectiveBinding } from 'vue'

interface RevealOptions {
  threshold?: number
  duration?: number
  delay?: number
  once?: boolean
  class?: string
  activeClass?: string
}

interface RevealElement extends HTMLElement {
  vRevealObserver?: IntersectionObserver
  vRevealOptions?: Required<RevealOptions>
}

const defaultOptions: RevealOptions = {
  threshold: 0.2,
  duration: 300,
  delay: 150,
  once: true,
  class: 'v-reveal',
}

export default {
  install: (Vue: App) => {
    Vue.directive('reveal', {
      beforeMount: (el: RevealElement, binding: DirectiveBinding<RevealOptions>) => {
        const opts = {
          ...defaultOptions,
          ...binding.value,
          activeClass: `${binding.value?.class || defaultOptions.class}-active`,
        } as Required<RevealOptions>

        el.vRevealOptions = opts

        el.classList.add(opts.class)
        el.style.transitionDuration = `${opts.duration}ms`
      },

      mounted: (el: RevealElement) => {
        const mergedOptions = el.vRevealOptions as Required<RevealOptions>

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const isVisible = entry.isIntersecting

              if (isVisible) {
                setTimeout(() => {
                  el.classList.add(mergedOptions.activeClass)
                  el.classList.remove(mergedOptions.class)

                  if (mergedOptions.once) {
                    observer.unobserve(el)
                  }
                }, mergedOptions.delay)
              } else if (!mergedOptions.once) {
                el.classList.remove(mergedOptions.activeClass)
                el.classList.add(mergedOptions.class)
              }
            })
          },
          { threshold: mergedOptions.threshold },
        )

        observer.observe(el)
        el.vRevealObserver = observer
      },

      unmounted: (el: RevealElement) => {
        const observer = el.vRevealObserver
        if (observer) {
          observer.unobserve(el)
          observer.disconnect()
        }

        delete el.vRevealOptions
        delete el.vRevealObserver
      },
    })
  },
}
